import { routes } from "@/seo/routes";
import { DEFAULT_LANG, LANGUAGES, SUPPORTED, UI_FALLBACK_LANG, isSupported } from "@/i18n/languages";
import { groupPathsIncludingDrafts, langOf } from "@/seo/locale";

export { SUPPORTED };

/**
 * D'où l'application tire la langue de son interface.
 *
 * Les pages de contenu portent leur langue dans leur URL : Google doit pouvoir
 * choisir. Les pages de l'application, non — /new et /game/<id> sont hors index,
 * et un lien de partie partagé doit s'ouvrir dans la langue de celui qui le
 * reçoit, pas dans celle de celui qui l'a envoyé. Leur langue se déduit donc,
 * dans cet ordre :
 *
 *   1. le choix explicite de l'utilisateur, s'il a déjà basculé la langue ;
 *   2. la dernière page de contenu qui revendique sa langue — en ou de, les
 *      pages françaises n'en déclarent pas ;
 *   3. la langue du navigateur, pour l'invité qui arrive droit sur une partie ;
 *   4. la dernière page de contenu traversée, français implicite compris cette
 *      fois — pour le visiteur entré par dartsync.fr dont le navigateur ne
 *      parle aucune des trois ;
 *   5. l'anglais — la même réponse que x-default et que la racine du .com.
 *
 * Sans navigateur du tout, au prérendu de /new, la page est celle du site : le
 * français, comme le titre et la description que seoConfig lui donne.
 *
 * Seul le choix explicite est mémorisé d'une visite à l'autre. La page
 * d'origine vaut le temps de la page chargée, la page traversée le temps de
 * l'onglet : persistées, elles deviendraient un choix que l'utilisateur n'a
 * jamais fait.
 */

const CHOICE_KEY = "dartsync_lang";

/** Le choix mémorisé, ou null. Le stockage lève en navigation privée. */
export function loadChoice() {
    try {
        const stored = localStorage.getItem(CHOICE_KEY);
        return isSupported(stored) ? stored : null;
    } catch {
        return null;
    }
}

export function saveChoice(lang) {
    try {
        localStorage.setItem(CHOICE_KEY, lang);
    } catch {
        // stockage indisponible : le choix ne vaut que pour la session
    }
}

const VISITED_KEY = "dartsync_visited_lang";

/**
 * La langue de la dernière page de contenu traversée dans cet onglet, ou null.
 *
 * En mémoire de session, pas dans une simple référence React : celle-ci mourait
 * au rechargement, et l'Italien entré par dartsync.fr qui rechargeait sa partie
 * retombait sur l'anglais. La session survit au rechargement du même onglet et
 * pas à un lien ouvert par un tiers, qui garde ainsi sa propre langue.
 */
export function loadVisited() {
    try {
        const stored = sessionStorage.getItem(VISITED_KEY);
        return isSupported(stored) ? stored : null;
    } catch {
        return null;
    }
}

export function saveVisited(lang) {
    try {
        sessionStorage.setItem(VISITED_KEY, lang);
    } catch {
        // stockage indisponible : l'indice ne vaut que pour la page chargee
    }
}

/**
 * Le navigateur à consulter : celui reçu en paramètre, sinon celui de la page ;
 * null au prérendu, où il n'y en a pas.
 */
function navigatorOf(nav) {
    // `window` et non `navigator` : node expose globalThis.navigator depuis la
    // version 21, et le lire au prerendu faisait rendre /new dans la langue de
    // la machine de build — anglais dans un conteneur sans LANG.
    return nav || (typeof window === "undefined" ? null : window.navigator);
}

/**
 * La langue du navigateur, si le site la sert.
 * `navigator.languages` est ordonné par préférence : on prend la première
 * connue plutôt que la première tout court, sinon un visiteur en fr-CA,en
 * ressortirait sans langue du tout.
 */
export function browserLanguage(nav) {
    const source = navigatorOf(nav);
    if (!source) return null;
    const candidates = source.languages && source.languages.length ? source.languages : [source.language];
    for (const tag of candidates) {
        if (!tag) continue;
        const base = String(tag).toLowerCase().split("-")[0];
        if (isSupported(base)) return base;
    }
    return null;
}

/**
 * La langue à servir sur une page de l'application.
 *
 * `routeLang` est la langue déclarée par la route courante, s'il y en a une :
 * sur une page de contenu elle tranche immédiatement, puisque l'URL est
 * explicite et qu'un choix mémorisé ne doit pas afficher de l'anglais sur
 * /fr/regles.
 */
export function resolveLanguage({ routeLang, choice, originLang, visitedLang, nav } = {}) {
    if (isSupported(routeLang)) return routeLang;
    if (isSupported(choice)) return choice;
    if (isSupported(originLang)) return originLang;
    const source = navigatorOf(nav);
    // Sans navigateur — le prerendu de /new — la page est celle du site.
    if (!source) return DEFAULT_LANG;
    const browser = browserLanguage(source);
    if (browser) return browser;
    // Les pages francaises ne declarent pas leur langue et ne comptent donc
    // pas comme origine — un Allemand qui en lit une faute de traduction garde
    // son allemand, tranche a l'etage precedent. Mais quand le navigateur ne
    // parle aucune des trois, avoir lu l'accueil francais est le seul indice
    // qu'un visiteur entre par dartsync.fr ait donne : il vaut mieux que le
    // repli generique.
    if (isSupported(visitedLang)) return visitedLang;
    return UI_FALLBACK_LANG;
}

/**
 * Les chemins de l'application, ceux dont l'URL ne porte pas de langue.
 *
 * Le marqueur est ici et non dans seoConfig : /new y figure déjà, en noindex,
 * et « hors index » ne veut pas dire « sans langue » — les mentions légales
 * pourraient être noindex sans cesser d'être françaises.
 */
const APP_PATH_PREFIXES = ["/new", "/game"];

/**
 * Le chemin tel que seoConfig le connait.
 *
 * Caddy sert /en/ depuis /en/index.html sans rediriger : les deux URL
 * fonctionnent, et React Router rapporte le chemin tel qu'il a ete tape. Sans
 * cette normalisation, /en/ n'etait pas reconnu comme une route, perdait sa
 * langue, et la page anglaise s'affichait avec une navigation francaise.
 */
export function normalisePath(path) {
    if (typeof path !== "string" || path === "") return "/";
    const trimmed = path.replace(/\/+$/, "");
    return trimmed === "" ? "/" : trimmed;
}

/**
 * L'ecran de l'application auquel ce chemin appartient, ou null.
 *
 * « new » ou « game », qui sont aussi les segments dont se composent les clefs
 * de libelle : seo.new.title, seo.game.title. Un seul endroit decide donc de ce
 * qu'est un ecran de l'application, et il sert a la fois a la resolution de la
 * langue et au titre d'onglet.
 */
export function appScreen(path) {
    const clean = normalisePath(path);
    const prefixe = APP_PATH_PREFIXES.find(
        (prefix) => clean === prefix || clean.startsWith(`${prefix}/`)
    );
    return prefixe ? prefixe.slice(1) : null;
}

export function isAppPath(path) {
    return appScreen(path) !== null;
}

/**
 * La clef de libelle du titre d'onglet, ou null pour une page de contenu.
 *
 * Trois routes dont l'URL ne porte pas de langue : les deux ecrans de
 * l'application, et la 404 que rend n'importe quelle URL morte. seoConfig ne
 * porte qu'une chaine francaise par route, donc aucune des trois n'y trouve son
 * titre — /new affichait du francais sous une interface allemande, /game et la
 * 404 gardaient celui d'app.html.
 *
 * Distincte d'`appScreen`, qui decide de la resolution de la langue et du
 * comportement du selecteur : la 404 a besoin d'un titre traduit, pas d'etre
 * traitee comme un ecran de l'application.
 */
export function titleKey(path) {
    const ecran = appScreen(path);
    if (ecran) return ecran;
    return normalisePath(path) === "/404" ? "notFound" : null;
}

/**
 * La langue déclarée par une route, ou null si elle n'en déclare pas.
 *
 * Trois cas à ne pas confondre : une page de contenu française, qui ne porte
 * pas de champ `lang` mais est bien française ; une page de l'application, qui
 * n'a pas de langue propre ; et une route inconnue. `langOf` répond « fr » aux
 * trois, ce qui est juste pour les données structurées et faux ici.
 */
export function declaredLang(path) {
    const clean = normalisePath(path);
    if (isAppPath(clean)) return null;
    if (!routes[clean]) return null;
    return langOf(clean);
}

/**
 * La langue que la route revendique explicitement, ou null.
 *
 * Distincte de `declaredLang`, qui complete par le francais : une page
 * francaise ne porte pas de champ `lang`, et enregistrer son « fr » implicite
 * comme langue d'origine condamnait l'etage navigateur de la chaine. Un invite
 * allemand qui suit un lien vers une page francaise — faute de version
 * allemande — ne demande pas a passer en francais.
 */
export function explicitLang(path) {
    const clean = normalisePath(path);
    if (isAppPath(clean)) return null;
    const declared = routes[clean]?.lang;
    return isSupported(declared) ? declared : null;
}

/**
 * Les versions de cette page dans chaque langue, par code de langue.
 *
 * Objet vide pour une page de l'application — /new n'a qu'une URL et sa langue
 * se déduit — et pour une page de contenu sans groupe, donc sans traduction.
 *
 * Les brouillons en font partie : `noindex` dit à Google de ne pas indexer, pas
 * à l'utilisateur de ne pas y aller. C'est aussi ce qui permet au sélecteur de
 * fonctionner avant que le domaine .com soit prêt.
 */
export function translationsOf(path) {
    const clean = normalisePath(path);
    if (isAppPath(clean)) return {};
    const group = routes[clean]?.group;
    if (!group) return {};
    return Object.fromEntries(groupPathsIncludingDrafts(group).map((p) => [langOf(p), p]));
}

/**
 * Ce qu'il faut faire quand on bascule vers `lang` depuis `path`.
 *
 * Trois réponses seulement, et c'est le chemin courant qui les départage :
 *
 *   - `choose` sur un écran de l'application. La bascule y change l'interface
 *     sous les yeux de l'utilisateur : c'est un choix, et il se mémorise.
 *   - `navigate` sur une page de contenu. Là, la traduction est une autre URL ;
 *     le choix ne se mémorise pas, parce que le geste n'a l'air que d'un
 *     changement de page. La langue suit quand même sur l'application, portée
 *     par `originLang` — troisième étage de `resolveLanguage`.
 *   - `none` quand on clique la langue déjà affichée.
 *
 * Le repli sur l'accueil de la langue vaut pour une page de contenu sans
 * traduction. Sans lui, le sélecteur disparaissait de ces pages — et elles
 * sont précisément où mènent les liens d'en-tête qui n'ont pas de version
 * traduite, donc un visiteur allemand y arrivait sans aucun moyen de repartir
 * dans sa langue. Une fois toutes les pages traduites, ce repli ne se
 * déclenche plus ; il reste le filet du jour où une page nouvelle n'a pas
 * encore ses traductions.
 */
export function switchAction(path, lang) {
    const clean = normalisePath(path);
    // Une langue que le site ne sert pas ne mène nulle part. En tête, et non
    // sur le résultat : `translationsOf` construit un objet ordinaire, donc
    // `targets["constructor"]` rendait la fonction Object — assez vraie pour
    // passer pour une destination. C'est le troisième endroit du projet où ce
    // piège se referme.
    if (!isSupported(lang)) return { kind: "none" };
    if (isAppPath(clean)) return { kind: "choose" };

    // La langue déjà affichée est inerte. Se comparer au chemin d'arrivée ne
    // suffisait pas : sur une page française sans traduction, cliquer
    // « Français » envoyait le lecteur à l'accueil, comme si le drapeau
    // l'avait éjecté de sa page.
    if (lang === langOf(clean)) return { kind: "none" };

    return { kind: "navigate", to: translationsOf(clean)[lang] || LANGUAGES[lang].home };
}
